/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MERCH_CARD } from 'pricing-pages-lib/constants/PurchaseMotionsLocations';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import styled from 'styled-components';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import Small from 'UIComponents/elements/Small';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import UITile from 'UIComponents/tile/UITile';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { calculatedPriceSelector } from '../../selectors/PricingSelectors';
import { hasMaxProductQuantityInProductInventorySelector, hasProductVersionInProductInventorySelector, productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, productForSkuIdSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../selectors/PurchaseMotionSelectors';
const StyledTile = styled(UITile).withConfig({
  displayName: "ExpandableChecklistItem__StyledTile",
  componentId: "s7h5g9-0"
})(["border-color:#7fd1de;justify-content:space-between;"]);
const StyledPrice = styled.div.withConfig({
  displayName: "ExpandableChecklistItem__StyledPrice",
  componentId: "s7h5g9-1"
})(["font-size:14px;margin-top:8px;margin-bottom:6px;"]);
const StyledStepperWrapper = styled(UIFormControl).withConfig({
  displayName: "ExpandableChecklistItem__StyledStepperWrapper",
  componentId: "s7h5g9-2"
})(["margin-left:16px;"]);
const StyledStepper = styled(UIStepperInput).withConfig({
  displayName: "ExpandableChecklistItem__StyledStepper",
  componentId: "s7h5g9-3"
})(["width:100px;"]);
const Bold = styled.b.withConfig({
  displayName: "ExpandableChecklistItem__Bold",
  componentId: "s7h5g9-4"
})(["font-size:14px;"]);

const ExpandableChecklistItem = ({
  skuId,
  selectedQuantity,
  onSelectedChange,
  use,
  queryParamTerm
}) => {
  const ownedQuantity = useSelector(reduxState => productsFromProductInventoryQuantitySelector(reduxState, {
    skuId
  }));
  const apiName = useSelector(state => productForSkuIdSelector(state, {
    skuId
  }).apiName);
  useEffect(() => {
    if (selectedQuantity < ownedQuantity) {
      onSelectedChange({
        value: ownedQuantity,
        skuId,
        apiName,
        previousValue: selectedQuantity
      });
    }
  }, [selectedQuantity, ownedQuantity]);
  const billingNameKey = useSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const currencyCode = useSelector(activeCurrencySelector);
  const isRecurring = useSelector(state => {
    const {
      chargeType
    } = productForSkuIdSelector(state, {
      skuId
    });
    return chargeType === RECURRING;
  });
  const quantifiable = useSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.quantifiable;
  });
  const singleUnitPrice = useSelector(state => {
    const pricingInfo = calculatedPriceSelector(state, {
      productConfigurations: [createProductConfig({
        skuId
      })],
      queryParamTerm
    });
    return isRecurring ? pricingInfo.monthly.calculatedPrice : pricingInfo.oneTimeFee.calculatedPrice;
  });
  const isTouchlessEligible = useSelector(reduxState => purchaseMotionsSelector(reduxState, {
    location: MERCH_CARD,
    skuId
  })).includes(BUY_TOUCHLESS);
  const ownsProduct = useSelector(reduxState => hasProductVersionInProductInventorySelector(reduxState, {
    skuId
  }));
  const ownsMaxQuantityOfProduct = useSelector(reduxState => hasMaxProductQuantityInProductInventorySelector(reduxState, {
    skuId
  }));
  const maxTouchlessPurchasableQuantity = useSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.maxTouchlessPurchasableQuantity;
  });
  const limit = maxTouchlessPurchasableQuantity === null ? Infinity : maxTouchlessPurchasableQuantity || 1;
  const canBuyMultiple = quantifiable && limit > 1;
  const disableTile = canBuyMultiple ? ownsMaxQuantityOfProduct : ownsProduct;
  const checked = selectedQuantity > ownedQuantity;
  return /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: canBuyMultiple ? 'pricingPage.planBuilder.addonsAndServices.addons.disabledTooltip.canOwnMultiple' : 'pricingPage.planBuilder.addonsAndServices.addons.disabledTooltip.canOwnOne'
    }),
    disabled: !disableTile,
    children: /*#__PURE__*/_jsxs(StyledTile, {
      distance: "extra-small",
      className: "flex-row p-all-4",
      children: [/*#__PURE__*/_jsx(UICheckbox, {
        disabled: disableTile,
        checked: checked,
        onChange: evt => {
          const localChecked = evt.target.checked;
          onSelectedChange({
            checked: localChecked,
            value: localChecked ? ownedQuantity + 1 : ownedQuantity,
            skuId,
            apiName,
            previousValue: localChecked ? ownedQuantity : selectedQuantity
          });
        },
        "data-selenium-class": `${use}-checklist-item`,
        "data-selected": checked,
        "data-touchless-eligible": isTouchlessEligible,
        "data-api-name": apiName,
        children: /*#__PURE__*/_jsxs("div", {
          className: "m-left-1",
          children: [/*#__PURE__*/_jsx(Bold, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `pricing-pages.products.${billingNameKey}.title`
            })
          }), /*#__PURE__*/_jsxs(StyledPrice, {
            children: [/*#__PURE__*/_jsx(FormattedCurrency, {
              value: singleUnitPrice,
              currencyCode: currencyCode
            }), isRecurring ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.planBuilder.priceDisplay.perMonth"
            }) : /*#__PURE__*/_jsx(Fragment, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                className: "m-left-1",
                message: "pricingPage.notes.oneTimeFeeParentheses"
              })
            })]
          }), /*#__PURE__*/_jsx(Small, {
            children: /*#__PURE__*/_jsx("p", {
              className: "m-bottom-2",
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: `pricing-pages.products.${billingNameKey}.paragraph`
              })
            })
          })]
        })
      }), canBuyMultiple && /*#__PURE__*/_jsx(StyledStepperWrapper, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.planBuilder.productPages.quantity"
        }),
        children: /*#__PURE__*/_jsx(StyledStepper, {
          disabled: disableTile,
          stepSize: 1,
          onChange: evt => {
            const value = evt.target.value;
            onSelectedChange({
              value,
              skuId,
              apiName,
              previousValue: selectedQuantity
            });
          },
          value: selectedQuantity,
          min: ownedQuantity,
          max: limit || null
        })
      })]
    })
  });
};

export default ExpandableChecklistItem;